import React, { useState } from "react"
import { TextField, Button, Checkbox, FormControlLabel } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import Typography from "@mui/material/Typography"
import Alert from "@mui/material/Alert"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import { useSnackbar } from "notistack"
import { updateMemberPassword, deleteMember, updateCommunityManager, requestPasswordResetEmail } from "services/member"
import { useForm } from "react-hook-form"
import {
  deactivateMember,
  reactivateMember,
  removeFreeTrial,
} from "services/member"
import { verifyResponse } from "utils/httpRequest"
import {
  MEMBER_STATUS_INDEX,
  TRIAL_STATUS_INDEX,
  TRIAL_STATUS,
} from "constants/global"
import MemberEmailUpdateDlg from "./MemberEmailUpdateDlg"
import { UseConfirmDialog } from "components/customHooks"
import { StyledMemberSubscriptions } from "./Member.style"

const MemberAccount = ({
  member,
  access,
  refreshMember,
  refreshSearch,
  onClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Account Action",
    description: "Are you sure deactivate this user?",
    agreeText: "Yes",
    disagreeText: "No",
  })

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm()

  const { enqueueSnackbar } = useSnackbar()

  const [openMemberEmailUpdateDlg, memberEmailUpdateDlg] = MemberEmailUpdateDlg({
    refreshMemberDetail: refreshMember,
  })

  const isEmailRegistrationExist = () =>
    !!(member.subId && member.subId !== "" && member.subId.includes("auth0"))

  const isEmailRegistrationOnly = () =>
    !!(
      member.subId &&
      member.subId !== "" &&
      member.subId.includes("auth0") &&
      member.appleThirdId === null &&
      member.googleThirdId === null &&
      member.facebookThirdId === null
    )

  const delMember = async (memberId) => {
    setLoading(true)
    try {
      const response = await deleteMember({ memberId })
      verifyResponse(response)

      if (response.rtnCode === 200) {
        enqueueSnackbar("Remove member account success.", {
          variant: "success",
        })
        refreshSearch()
        setTimeout(() => {
          onClose()
        }, 600)
      } else {
        enqueueSnackbar(`Fail to delete member: ${response.message}`, {
          variant: "warning",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (data) => {
    await changeMemberPassword(data.password)
    reset()
  }

  const sendRequestEmailForPasswordReset = async () => {
    setLoading(true)
    try {
      const response = await requestPasswordResetEmail({
        memberId: member.userId,
      })
      verifyResponse(response)

      if (response.rtnCode === 200 || response.success) {
        enqueueSnackbar("Password reset email sent successfully", {
          variant: "success",
        })
      } else {
        enqueueSnackbar("Fail to send password reset email", {
          variant: "warning",
        })
      }
    } catch (error) {
      console.error("sendRequestEmailForPasswordReset error: ", error)
      enqueueSnackbar("Error sending password reset email", {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const openUpdateEmailModal = () => {
    openMemberEmailUpdateDlg(member.userId)
  }

  const changeMemberPassword = async (password) => {
    setLoading(true)
    try {
      const response = await updateMemberPassword({
        memberId: member.userId,
        newPassword: password,
      })
      verifyResponse(response)

      if (response.rtnCode === 200) {
        enqueueSnackbar("Reset Password Success", {
          variant: "success",
        })
      } else {
        enqueueSnackbar(response.message, {
          variant: "warning",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const deactivateMemberAction = async (memberId) => {
    setLoading(true)
    try {
      const response = await deactivateMember({ id: memberId })
      verifyResponse(response)
      refreshMember()
    } finally {
      setLoading(false)
    }
  }

  const reactivateMemberAction = async (memberId) => {
    setLoading(true)
    try {
      const response = await reactivateMember({ id: memberId })
      verifyResponse(response)
      refreshMember()
    } finally {
      setLoading(false)
    }
  }

  const endFreeTrial = async (memberId) => {
    setLoading(true)
    try {
      const response = await removeFreeTrial(memberId)
      verifyResponse(response)
      enqueueSnackbar("Free trial removed.", { variant: "success" })
      await refreshMember()
    } finally {
      setLoading(false)
    }
  }

  const toggleCommunityManager = async () => {
    try {
      setLoading(true)
      const next = member.isCommunityManager === 1 ? 0 : 1
      await updateCommunityManager({
        memberId: member.userId,
        isCommunityManager: next,
      })
      enqueueSnackbar(
        next === 1 ? "Granted Community Manager" : "Revoked Community Manager",
        { variant: "success" }
      )
      await refreshMember()
    } catch (e) {
      enqueueSnackbar("Failed to update Community Manager status", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card className="section-card">
        <CardContent>
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <StyledMemberSubscriptions>
      <Card className="section-card">
        <CardContent>
          <h3 className="section-title">Account Modifications</h3>
          <p className="section-subtitle">
            Manage account-level actions, community manager state, and member status.
          </p>

          <div className="account-row">
            <div className="account-item">
              <div className="account-label">Email</div>
              <div className="account-value">{member.email || "N/A"}</div>
            </div>
            <div className="account-item">
              <div className="account-label">Member Nickname</div>
              <div className="account-value">{member.nickName || "N/A"}</div>
            </div>
          </div>

          <div className="community-toggle">
            <FormControlLabel
              control={
                <Checkbox
                  checked={member?.isCommunityManager === 1}
                  onChange={toggleCommunityManager}
                  disabled={loading}
                />
              }
              label={`Community Manager ${member?.isCommunityManager === 1 ? "(Active)" : "(Inactive)"}`}
            />
          </div>

          {member && isEmailRegistrationExist() && (
            <Typography component="div" className="button-block">
              <Button
                color="success"
                variant="contained"
                onClick={sendRequestEmailForPasswordReset}
              >
                Request Password Reset Email for User
              </Button>
            </Typography>
          )}

          {member && isEmailRegistrationOnly() && (
            <Typography component="div" className="button-block">
              <Button
                color="success"
                variant="contained"
                onClick={openUpdateEmailModal}
              >
                Update Account Email
              </Button>
            </Typography>
          )}

          <Typography className="danger-note">
            {member.isActive === MEMBER_STATUS_INDEX.ACTIVATE ? (
              <>
                <strong>Warning:</strong> Once the account is deactivated, the member
                cannot use the system normally.
              </>
            ) : (
              <>Member account is deactivated. You can reactivate it here.</>
            )}
          </Typography>

          <div className="button-block">
            {access.memberDeactivateAccess &&
              member.isActive === MEMBER_STATUS_INDEX.ACTIVATE && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    openConfirmDialog(() => {
                      deactivateMemberAction(member.userId)
                    })
                  }}
                >
                  Deactivate User
                </Button>
              )}

            {access.memberReactivateAccess &&
              member.isActive === MEMBER_STATUS_INDEX.DEACTIVATE && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    openConfirmDialog(() => {
                      reactivateMemberAction(member.userId)
                    }, "Are you sure reactivate this user?")
                  }}
                >
                  Reactivate User
                </Button>
              )}
          </div>
        </CardContent>
      </Card>

      {member && isEmailRegistrationExist() && (
        <Card className="section-card">
          <CardContent>
            <h3 className="section-title">Password Reset</h3>
            <p className="section-subtitle">Set a new password for this account.</p>

            {(errors.password || errors.confirmPassword) && (
              <Alert severity="warning">
                <ul>
                  {errors.password && <li>{errors.password.message}</li>}
                  {errors.confirmPassword && (
                    <li>{errors.confirmPassword.message}</li>
                  )}
                </ul>
              </Alert>
            )}

            <form
              onSubmit={handleSubmit(handleAction)}
              className="reset-password-form"
            >
              <TextField
                type="password"
                label="Password"
                variant="outlined"
                size="small"
                {...register("password", { required: "Password is required" })}
              />
              <TextField
                type="password"
                label="Confirm Password"
                variant="outlined"
                size="small"
                {...register("confirmPassword", {
                  required: "Confirm password is required",
                  validate: (val) => {
                    if (watch("password") !== val) {
                      return "Your passwords do no match"
                    }
                  },
                })}
              />
              <Button type="submit" size="large" variant="contained">
                Reset
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="section-card">
        <CardContent>
          <h3 className="section-title">Free Trial</h3>
          <Typography sx={{ fontSize: 13 }} color="text.secondary" gutterBottom>
            Free Trial Status:
          </Typography>
          <Typography variant="body1" component="div" sx={{ fontWeight: 700 }}>
            {TRIAL_STATUS[member.trialStatus].text}
          </Typography>

          {access.memberRemoveFreeTrial &&
            member.trialStatus !== TRIAL_STATUS_INDEX.EXPIRED && (
              <div className="button-block">
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    openConfirmDialog(() => {
                      endFreeTrial(member.userId)
                    }, "Are you sure remove the free trial?")
                  }}
                >
                  Remove Free Trial
                </Button>
              </div>
            )}
        </CardContent>
      </Card>

      <Card className="section-card">
        <CardContent>
          <h3 className="section-title">Delete Account</h3>
          <Typography className="danger-note">
            <strong>Warning:</strong> This legacy action disables the account and deletes
            Auth0/push records only. Use DSAR erasure for privacy deletion requests.
          </Typography>

          {access.memberDeleteAccess && (
              <div className="button-block">
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    openConfirmDialog(() => {
                      delMember(member.userId)
                    }, "Are you sure want to delete this member account.")
                  }}
                >
                  Delete Member Account
                </Button>
              </div>
            )}
        </CardContent>
      </Card>

      {ConfirmDialog}
      {memberEmailUpdateDlg}
    </StyledMemberSubscriptions>
  )
}

export default MemberAccount
